import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useGetTransportsQuery } from './etlIntegrationsApi';

type ResourcePanelView = 'new' | 'list_requests' | 'air_ambulance';

/** Panel views that show a specific resource type detail, form, or status. */
type ResourceRequestDetailView = Exclude<
  ResourcePanelView,
  'new' | 'list_requests'
>;

interface ResourceRequestsState {
  selectedRequest: string | null;
  isResourcePanelOpen: boolean;
  isEditing: boolean;
  resourcePanelView: ResourcePanelView;
  seenTransportIds: string[];
  breadCumbs: ResourcePanelView[];
}

const initialState: ResourceRequestsState = {
  selectedRequest: null,
  isResourcePanelOpen: false,
  isEditing: false,
  resourcePanelView: 'list_requests',
  seenTransportIds: [],
  breadCumbs: [],
};

/**
 * The result of the getTransports query, which should be an array of transports.
 */
type TransportQueryResult = {
  transports?: {
    gmr_transport_id: unknown;
  }[];
};

const getTransportId = (transport: unknown): string | undefined => {
  if (transport === null || typeof transport !== 'object') {
    return undefined;
  }

  const transportRecord = transport as {
    gmr_transport_id: unknown;
  };

  const transportId = transportRecord.gmr_transport_id;
  if (typeof transportId !== 'string' || transportId.trim().length === 0) {
    return undefined;
  }

  return transportId;
};

/**
 * Extract non-empty transport ids from the GMR transport response.
 */
const getTransportIds = (result: TransportQueryResult): string[] => {
  if (!Array.isArray(result.transports)) {
    return [];
  }

  return result.transports
    .map(transport => getTransportId(transport))
    .filter((transportId): transportId is string => Boolean(transportId));
};

/**
 * Manages the UI state for the right-hand resource requests panel. See
 * etlIntegrationsApi & hooks for the actual resource request data (e.g. GMR/Air
 * Ambulance transports) are cached under the api key in redux.
 */
const resourceRequestsSlice = createSlice({
  name: 'resourceRequests',
  initialState,
  reducers: {
    setSelectedRequest: (state, action: PayloadAction<string | null>) => {
      state.selectedRequest = action.payload;
      state.isEditing = false;
    },
    setIsEditing: (state, action: PayloadAction<boolean>) => {
      state.isEditing = action.payload;
    },
    openResourcePanel: state => {
      state.isResourcePanelOpen = true;
      state.resourcePanelView = 'list_requests';
      state.breadCumbs = [...initialState.breadCumbs];
    },
    closeResourcePanel: state => {
      state.isResourcePanelOpen = false;
      state.selectedRequest = null;
      state.isEditing = false;
      state.resourcePanelView = 'list_requests';
      state.breadCumbs = [...initialState.breadCumbs];
    },
    setResourcePanelView: (state, action: PayloadAction<ResourcePanelView>) => {
      state.resourcePanelView = action.payload;

      const index = state.breadCumbs.indexOf(action.payload);
      if (index > -1) {
        // If navigating to a previous bread crumb remove any bread crumbs
        // afterwards
        state.breadCumbs = state.breadCumbs.slice(0, index + 1);
      } else {
        // Add a new bread crumb
        state.breadCumbs = [...state.breadCumbs, action.payload];
      }
    },
    markTransportSeen: (state, action: PayloadAction<string>) => {
      if (!state.seenTransportIds.includes(action.payload)) {
        state.seenTransportIds.push(action.payload);
      }
    },
    toggleResourcePanel: state => {
      state.isResourcePanelOpen = !state.isResourcePanelOpen;
      if (state.isResourcePanelOpen && state.breadCumbs.length <= 0) {
        state.breadCumbs = [state.resourcePanelView];
      }
    },
  },
  selectors: {
    selectSelectedRequestId: state => state.selectedRequest,
    selectIsResourcePanelOpen: state => state.isResourcePanelOpen,
    selectIsEditing: state => state.isEditing,
    selectResourcePanelView: state => state.resourcePanelView,
    selectSeenTransportIds: state => state.seenTransportIds,
    selectBreadCrumbs: state => state.breadCumbs,
  },
});

export const {
  setSelectedRequest,
  setIsEditing,
  openResourcePanel,
  closeResourcePanel,
  setResourcePanelView,
  toggleResourcePanel,
  markTransportSeen,
} = resourceRequestsSlice.actions;

const selectors = resourceRequestsSlice.selectors;

export const useSelectedRequestId = () =>
  useSelector(selectors.selectSelectedRequestId);
export const useIsResourcePanelOpen = () =>
  useSelector(selectors.selectIsResourcePanelOpen);
export const useIsEditingResourceRequest = () =>
  useSelector(selectors.selectIsEditing);
export const useResourcePanelView = () =>
  useSelector(selectors.selectResourcePanelView);
export const useSeenTransportIds = () =>
  useSelector(selectors.selectSeenTransportIds);
export const useBreadCrumbs = () => useSelector(selectors.selectBreadCrumbs);
/**
 * Returns true when there are transport requests not yet marked as seen.
 * This checks only the GMR transport feed used by the portal
 * resource request panel, because that is the current source of truth for
 * unread request indicators.
 */
export const useHasUnreadTransportRequests = (): boolean => {
  const seenTransportIds = useSelector(selectors.selectSeenTransportIds);
  const { data, currentData, isError } = useGetTransportsQuery(undefined, {
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  const transportsResult = useMemo(() => {
    if (!currentData && !data) {
      return null;
    }
    return currentData ?? data;
  }, [currentData, data]);

  const transportIds = useMemo(() => {
    if (!transportsResult) {
      return [];
    }
    return getTransportIds(transportsResult);
  }, [transportsResult]);

  return useMemo(() => {
    if (isError && !transportsResult) {
      return false;
    }

    if (transportIds.length === 0) {
      return false;
    }

    const seenTransportIdsSet = new Set(seenTransportIds);

    return transportIds.some(
      transportId => !seenTransportIdsSet.has(transportId),
    );
  }, [isError, seenTransportIds, transportIds, transportsResult]);
};

export type { ResourcePanelView, ResourceRequestDetailView };
export const resourceRequests = resourceRequestsSlice.reducer;
